import SportsEsportsIcon from "@mui/icons-material/SportsEsports";
import { Box, Typography } from "@mui/material";
import { motion } from "framer-motion";
import { useMemo } from "react";
import { Link as RouterLink } from "react-router-dom";
import ArcadeSongCard from "../../arcade/ArcadeSongCard";
import useWindowSize from "../../../hooks/useWindowSize";
import { usePrefersReducedMotion } from "../../../hooks/useMotionPreferences";
import { lc, trackClick } from "../../_helpers";
import { ARCADE_GAME_KINDS, ARCADE_GAMES } from "../../../utils/arcade/games";
import { arcadePath, buildArcadeLineup, homepageArcadePicks, type ArcadeReleaseInput, type ArcadeTopTrackInput } from "../../../utils/arcade/lineup";

interface GamesSectionProps {
  artistData?: { name?: string; topTracks?: ArcadeTopTrackInput[] } | null;
  song?: { trackData?: { albums?: ArcadeReleaseInput[] } } | null;
}

export function gamesSectionEnabled(artistName: string | undefined | null): boolean {
  return lc(artistName || "") === "truevined";
}

// Homepage card for the arcade: popular songs first, then the newest release.
export function GamesSection({ artistData, song }: GamesSectionProps) {
  const windowSize = useWindowSize();
  const reducedMotion = usePrefersReducedMotion();
  const enabled = gamesSectionEnabled(artistData?.name);
  const albums = song?.trackData?.albums;
  const picks = useMemo(
    () => (enabled ? homepageArcadePicks(buildArcadeLineup(artistData?.topTracks, albums)) : []),
    [enabled, artistData?.topTracks, albums],
  );

  if (!enabled || picks.length === 0) return null;

  return (
    <Box
      component="section"
      className="GamesSection"
      aria-labelledby="games-heading"
      sx={{
        position: "relative",
        width: `calc(${windowSize.width}px - (2.5rem * 2))`,
        p: { xs: "4rem 2rem", md: "3.5rem" },
        m: { xs: "4rem 2.5rem 0", md: "12.25rem 2.5rem 0" },
        backgroundColor: "var(--color5)",
        borderRadius: "1.875rem",
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: { xs: "2.5rem", md: "2.5rem" },
        zIndex: 1,
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden
        sx={{
          position: "absolute",
          inset: 0,
          pointerEvents: "none",
          opacity: 0.5,
          background:
            "radial-gradient(60% 80% at 100% 0%, color-mix(in srgb, var(--logoColor) 16%, transparent), transparent 70%), repeating-linear-gradient(0deg, transparent 0 22px, color-mix(in srgb, var(--logoColor) 6%, transparent) 22px 23px)",
        }}
      />
      <Box
        sx={{
          position: "relative",
          width: "100%",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          gap: { xs: "2rem", md: "2.5rem" },
          flexWrap: "wrap",
        }}
      >
        <Box sx={{ minWidth: 0, maxWidth: "640px" }}>
          <Box sx={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <SportsEsportsIcon aria-hidden sx={{ color: "var(--logoColor2)", fontSize: { xs: "3rem", md: "2.6rem" } }} />
            <Typography
              id="games-heading"
              component="h2"
              sx={{ color: "var(--logoColor2)", fontSize: { xs: "3rem", md: "3.34rem" }, fontFamily: "Hanson", fontWeight: 700, m: 0, lineHeight: 1 }}
            >
              ARCADE
            </Typography>
          </Box>
          <Typography sx={{ mt: "12px", color: "var(--logoColor)", fontFamily: "Poppins, sans-serif", fontSize: { xs: "16px", md: "clamp(14px, 1rem, 17px)" }, lineHeight: 1.55 }}>
            Every song is a game. {ARCADE_GAME_KINDS.length} arcade games that move to the beat, dressed in each release's cover art.
          </Typography>
          <Box sx={{ mt: "12px", display: "flex", flexWrap: "wrap", gap: "8px" }}>
            {ARCADE_GAME_KINDS.map((kind) => (
              <Box
                key={kind}
                sx={{
                  px: "10px",
                  py: "4px",
                  borderRadius: "999px",
                  border: "1px solid color-mix(in srgb, var(--logoColor) 30%, transparent)",
                  color: "var(--logoColor)",
                  fontFamily: "Poppins, sans-serif",
                  fontSize: "12px",
                  fontWeight: 600,
                }}
              >
                {ARCADE_GAMES[kind].name}
              </Box>
            ))}
          </Box>
        </Box>
        <Box
          component={RouterLink}
          to={arcadePath()}
          onClick={() => trackClick("arcade:home-all", { label: "Artist page arcade link", section: "arcade" })}
          sx={{
            display: "inline-flex",
            alignItems: "center",
            minHeight: "44px",
            px: "20px",
            borderRadius: "999px",
            color: "var(--color5)",
            backgroundColor: "var(--logoColor2)",
            fontSize: { xs: "16px", md: "clamp(15px, 1.1rem, 18px)" },
            fontFamily: "Poppins",
            fontWeight: 700,
            textDecoration: "none",
            transition: "opacity 0.2s ease",
            "&:hover": { opacity: 0.85, color: "var(--color5)" },
            "&:focus-visible": { outline: "2px solid var(--logoColor2)", outlineOffset: "4px" },
          }}
        >
          Open the arcade
        </Box>
      </Box>

      <Box
        sx={{
          position: "relative",
          width: "100%",
          display: "grid",
          gridTemplateColumns: { xs: "repeat(2, minmax(0, 1fr))", md: `repeat(${picks.length}, minmax(0, 1fr))` },
          gap: { xs: "14px", md: "2rem" },
        }}
      >
        {picks.map((pick, index) => (
          <motion.div
            key={pick.slug}
            initial={reducedMotion ? false : { opacity: 0, y: 22 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "0px 0px 240px 0px" }}
            transition={{ duration: 0.5, delay: reducedMotion ? 0 : index * 0.08, ease: [0.23, 1, 0.32, 1] }}
            style={{ minWidth: 0 }}
          >
            <ArcadeSongCard song={pick} clickTarget="arcade:home-song" />
          </motion.div>
        ))}
      </Box>
    </Box>
  );
}

export default GamesSection;
